"use client";

import React, { useState, useRef, useEffect } from "react";
import { Activity, Cpu, Globe, Network, X } from "lucide-react";
import { Button } from "@/components/ui/button";
const hubs = [{
  id: "sv",
  name: "Silicon Valley",
  region: "US-West-1",
  x: 26,
  y: 38,
  capacity: "8.4 Tbps",
  utilization: "76%",
  latency: "2.1ms",
  status: "Active"
}, {
  id: "frankfurt",
  name: "Frankfurt",
  region: "EU-Central-1",
  x: 46,
  y: 30,
  capacity: "12.2 Tbps",
  utilization: "84%",
  latency: "4.8ms",
  status: "Expanding"
}, {
  id: "shenzhen",
  name: "Shenzhen HQ",
  region: "APAC-South-1",
  x: 84,
  y: 40,
  capacity: "25.6 Tbps",
  utilization: "91%",
  latency: "< 0.8ms",
  status: "Active"
}];
export default function GlobalInfrastructureMap() {
  const [activeHub, setActiveHub] = useState<string | null>(null);
  const mapRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (mapRef.current && !mapRef.current.contains(event.target as Node)) {
        setActiveHub(null);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  return <section data-section-id="143367" id="global-infrastructure-impact" className="dark bg-background text-foreground relative w-full overflow-hidden py-24 lg:py-32">
      {}
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(to_right,#122b3a0a_1px,transparent_1px),linear-gradient(to_bottom,#122b3a0a_1px,transparent_1px)] bg-[size:24px_24px]" />
      
      {}
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_center,var(--primary)_0%,transparent_100%)] opacity-[0.05]" />

      <div className="container relative z-10 mx-auto px-4 md:px-6">
        <div className="mb-16 flex flex-col items-center text-center">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-xs font-semibold text-primary font-mono uppercase tracking-widest">
            <Globe className="size-3.5" />
            <span>Global Operations</span>
          </div>
          <h2 className="font-sans text-4xl font-bold tracking-tighter text-foreground md:text-5xl lg:text-6xl">
            Global Infrastructure Impact
          </h2>
          <p className="mt-4 max-w-2xl text-muted-foreground font-sans md:text-lg">
            Strategic distribution of high-performance optical networks and AI data centers, optimized for minimal attenuation and maximum throughput across critical global corridors.
          </p>
        </div>

        {}
        <div ref={mapRef} className="relative mx-auto w-full max-w-6xl rounded-xl border border-border/40 bg-card/40 p-4 shadow-2xl backdrop-blur-md lg:p-8">
          {}
          <div className="relative w-full pb-[50%] min-h-[300px] md:min-h-[400px] lg:min-h-[500px]">
            
            {}
            <svg viewBox="0 0 1000 500" className="absolute inset-0 h-full w-full drop-shadow-2xl" preserveAspectRatio="xMidYMid meet" xmlns="http://www.w3.org/2000/svg">
              {}
              <g fill="#1a2635" stroke="rgba(0,255,255,0.08)" strokeWidth="1.5">
                {}
                <path d="M150,100 Q200,80 250,120 T300,150 Q280,200 250,250 T180,220 Q120,180 150,100 Z" />
                <path d="M250,250 Q270,280 290,300 T320,350 Q300,380 280,350 T250,250 Z" />
                {}
                <path d="M290,300 Q330,320 350,380 T320,480 Q280,450 270,380 T290,300 Z" />
                {}
                <path d="M450,120 Q500,100 550,130 T580,180 Q520,200 480,180 T450,120 Z" />
                {}
                <path d="M480,180 Q550,180 580,250 T550,380 Q480,350 450,280 T480,180 Z" />
                {}
                <path d="M550,130 Q650,80 750,120 T850,180 Q800,250 700,280 T580,180 Z" />
                <path d="M750,120 Q850,100 900,150 T850,220 Q800,200 750,120 Z" />
                {}
                <path d="M780,350 Q850,320 880,380 T820,450 Q750,420 780,350 Z" />
              </g>
            </svg>

            {}
            {hubs.map((hub, i) => {
            const isActive = activeHub === hub.id;
            return <div key={hub.id} data-index={i} className="absolute z-20" style={{
              left: `${hub.x}%`,
              top: `${hub.y}%`
            }}>
                  {}
                  <Button onClick={() => setActiveHub(isActive ? null : hub.id)} className="group relative -ml-3 -mt-3 flex size-6 items-center justify-center rounded-full p-0 hover:bg-transparent bg-transparent outline-none border-0" aria-label={`View details for ${hub.name}`} aria-expanded={isActive} variant="ghost">
                    {}
                    <span className="absolute inset-0 rounded-full bg-primary/30 animate-ping duration-3000" />
                    {}
                    <span className={`relative size-2.5 rounded-full transition-all duration-300 ${isActive ? 'bg-primary scale-150 shadow-[0_0_20px_var(--primary)]' : 'bg-primary/70 group-hover:bg-primary group-hover:scale-125'}`} />
                  </Button>

                  {}
                  {isActive && <div className="absolute left-1/2 top-full mt-4 w-64 -translate-x-1/2 animate-in fade-in zoom-in-95 duration-200 md:left-full md:top-1/2 md:mt-0 md:ml-4 md:-translate-y-1/2">
                      <div className="relative overflow-hidden rounded-lg border border-primary/40 bg-popover/95 p-4 shadow-2xl backdrop-blur-xl">
                        {}
                        <div className="absolute inset-x-0 top-0 h-[2px] bg-gradient-to-r from-transparent via-primary to-transparent opacity-60" />
                        
                        <div className="mb-3 flex items-start justify-between">
                          <div>
                            <h3 className="font-sans text-lg font-bold text-foreground leading-none">
                              {hub.name}
                            </h3>
                            <p className="mt-1 font-mono text-[10px] text-muted-foreground uppercase tracking-widest">
                              {hub.region}
                            </p>
                          </div>
                          <Button variant="ghost" size="icon" className="size-6 rounded-full hover:bg-primary/20 hover:text-primary transition-colors" onClick={e => {
                      e.stopPropagation();
                      setActiveHub(null);
                    }}>
                            <X className="size-3" />
                            <span className="sr-only">Close</span>
                          </Button>
                        </div>

                        <div className="space-y-2 font-mono text-xs">
                          <div className="flex items-center justify-between rounded bg-muted/30 px-2 py-1.5 border border-border/20">
                            <span className="flex items-center gap-1.5 text-muted-foreground">
                              <Activity className="size-3.5 text-primary" />
                              Throughput
                            </span>
                            <span className="font-bold text-foreground">{hub.capacity}</span>
                          </div>
                          <div className="flex items-center justify-between rounded bg-muted/30 px-2 py-1.5 border border-border/20">
                            <span className="flex items-center gap-1.5 text-muted-foreground">
                              <Cpu className="size-3.5 text-primary" />
                              Node Load
                            </span>
                            <span className="font-bold text-foreground">{hub.utilization}</span>
                          </div>
                          <div className="flex items-center justify-between rounded bg-muted/30 px-2 py-1.5 border border-border/20">
                            <span className="flex items-center gap-1.5 text-muted-foreground">
                              <Network className="size-3.5 text-primary" />
                              Sync Rate
                            </span>
                            <span className="font-bold text-foreground">{hub.latency}</span>
                          </div>
                        </div>
                        
                        <div className="mt-3 flex items-center gap-2 border-t border-border/30 pt-3">
                          <span className="relative flex size-2">
                            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-75"></span>
                            <span className="relative inline-flex size-2 rounded-full bg-primary"></span>
                          </span>
                          <span className="font-mono text-[10px] text-primary uppercase font-bold tracking-widest">
                            {hub.status}
                          </span>
                        </div>
                      </div>
                    </div>}
                </div>;
          })}
          </div>
        </div>
      </div>
    </section>;
}